function getUrlParam(name: string) {
  return new URLSearchParams(window.location.search).get(name);
}

export function getCallbackUrl() {
  return getUrlParam("callback");
}

/** `/login` com o mesmo callback, CODIFICADO — senão o primeiro "&" do destino vira parâmetro do login. */
export function loginUrlComCallback(callback: string | null | undefined) {
  return callback ? `/login?callback=${encodeURIComponent(callback)}` : "/login";
}

// Destinos para onde o login pode devolver o usuário (ClickUp 17ttyenaxv4), POR AMBIENTE: o cookie
// de sessão do SSO é do domínio do ambiente (.segfy.com em prod, .segfybeta.com em qa,
// .segfy.works em dev), então destino fora dele nunca funcionou logado — e aceitar qualquer um
// seria open redirect: login.segfy.com/...?callback=<site falso> usando a marca logo depois da senha.
// Ambiente desconhecido ou ausente = regra de produção.
const DOMINIOS_POR_AMBIENTE: Record<string, string[]> = {
  prod: ["segfy.com"],
  qa: ["segfybeta.com", "homol-app.segfy.com"], // homol-app usa o login de beta
  staging: ["segfybeta.com", "homol-app.segfy.com"],
  dev: ["segfy.works"],
};
// localhost só onde há dev local (o segfyvue local usa o login de beta)
const AMBIENTES_COM_LOCALHOST = ["qa", "staging", "dev", "local"];

/** Callback aceito: URL https num domínio da Segfy do ambiente, sem usuário/senha embutidos. */
export function callbackPermitido(callback: string | null | undefined, ambiente?: string) {
  if (!callback) return false;

  let url: URL;
  try {
    url = new URL(callback);
  } catch {
    return false; // relativo, "//host" sem esquema, lixo
  }

  if (url.username || url.password) return false; // https://evil@app.segfy.com — engana o olho

  const host = url.hostname.toLowerCase();
  if (host === "localhost" || host === "127.0.0.1") {
    return AMBIENTES_COM_LOCALHOST.includes(ambiente ?? "") && (url.protocol === "http:" || url.protocol === "https:");
  }

  const dominios = DOMINIOS_POR_AMBIENTE[ambiente ?? ""] ?? DOMINIOS_POR_AMBIENTE.prod;
  return url.protocol === "https:" && dominios.some((dominio) => host === dominio || host.endsWith("." + dominio));
}

function mesmoHost(a: string, b: string) {
  try {
    return new URL(a).host.toLowerCase() === new URL(b).host.toLowerCase();
  } catch {
    return false;
  }
}

// Parâmetros de campanha que a ferramenta de e-mail/anúncio põe no LINK DE LOGIN (e não no destino):
// seguem para o destino, para a contratação não aparecer como tráfego direto.
const PARAMETROS_DE_CAMPANHA = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function comCampanha(destino: string, linkDeLogin?: URLSearchParams) {
  const url = new URL(destino);
  PARAMETROS_DE_CAMPANHA.forEach((nome) => {
    const valor = linkDeLogin?.get(nome);
    if (valor && !url.searchParams.has(nome)) url.searchParams.set(nome, valor);
  });
  return url.href; // normalizado: navega exatamente para o que foi validado
}

/**
 * Para onde ir depois do login.
 *
 * A API devolve a PLATAFORMA do usuário (X-Redirect-To: app Segfy ou Multify) e, antes, ela
 * vencia sempre — `login?callback=<loja>?modulo=...` levava à raiz do app e o link da campanha
 * se perdia. Agora o callback vence quando aponta para a MESMA plataforma (o destino exato,
 * com a query e os utm); para outra plataforma, segue valendo a da API.
 */
export function destinoPosLogin(
  callback: string | null | undefined,
  plataforma: string | null | undefined,
  ambiente?: string,
  padrao?: string,
  linkDeLogin?: URLSearchParams
): string | null {
  const callbackOk = callbackPermitido(callback, ambiente);

  if (plataforma) {
    return callbackOk && mesmoHost(callback as string, plataforma)
      ? comCampanha(callback as string, linkDeLogin)
      : plataforma;
  }

  if (callbackOk) return comCampanha(callback as string, linkDeLogin);
  return padrao || null;
}

export function redirectAfterLogin(plataforma: string | null | undefined) {
  const destino = destinoPosLogin(
    getCallbackUrl(),
    plataforma,
    process.env.REACT_APP_SEGFY_ENVIRONMENT,
    // sem plataforma e sem callback válido a tela não pode ficar parada em "Entrando..."
    process.env.REACT_APP_DEFAULT_REDIRECT || process.env.REACT_APP_MANAGEMENT_SOFTWARE_SEGFY || appDoAmbiente(process.env.REACT_APP_SEGFY_ENVIRONMENT),
    new URLSearchParams(window.location.search)
  );

  if (destino) redirect(destino);
}

/** App do ambiente — último recurso quando nem o build nem a API dizem para onde ir. */
export function appDoAmbiente(ambiente?: string) {
  if (ambiente === "qa" || ambiente === "staging") return "https://app.segfybeta.com";
  if (ambiente === "dev") return "https://app.segfy.works";
  return "https://app.segfy.com";
}

export function redirect(url: string) {
  window.location.href = url;
}

const SIGNUP_URL_PROD = "https://signup.segfy.com";
const SIGNUP_URL_BETA = "https://sellerfy.segfybeta.com";

// URL de cadastro ("Assine agora"). Só o ambiente de QA/staging usa o segfybeta;
// prod (e qualquer outro ambiente) mantém o link de produção por padrão.
export function getSignupUrl() {
  const env = process.env.REACT_APP_SEGFY_ENVIRONMENT;
  const betaEnvs = ["qa", "staging"];

  if (env && betaEnvs.includes(env)) {
    return SIGNUP_URL_BETA;
  }

  return SIGNUP_URL_PROD;
}
